import { useCallback, useEffect, useState, useMemo } from 'react';
import { formatDate, parseDate, formatForInput } from '../../utils/dateUtils';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClinicalDataCard from './ClinicalDataCard';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { ToggleButton, ToggleButtonGroup, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { operationAPI } from '../../services/api';
import PlatformService from '../../services/PlatformService';

const validationSchema = Yup.object({
  operation_date: Yup.date().required('Required'),
  operation_type: Yup.string().required('Required'),
  surgeon: Yup.string().nullable(),
  hospital: Yup.string().nullable(),
  procedure_details: Yup.string().nullable(),
  complications: Yup.string().nullable(),
  outcome: Yup.string().nullable(),
  my_notes: Yup.string().nullable(),
});

const resolveOperationId = (operation) => operation?.id_op ?? null;

const formatOperationField = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : '—';
  }
  return String(value);
};

const formatOperationDate = (value) => formatDate(value);

// Real-time subscriptions removed with Supabase migration
const useOperationsSubscription = () => {
  // No-op for now
};

const toDateInputValue = (value) => formatForInput(value);

const formatTimestamp = (value) => {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleString('el-GR');
};

/**
 * High-fidelity Operation Report View Component
 * Provides a professional, hospital-grade preview of surgical data.
 */
const OperationReportView = ({ operation, patientName, age, gender, amka, phone, address, diagnosis }) => {
  const reportDate = formatDate(new Date());
  const surgeryDateStr = formatDate(operation.operation_date);

  return (
    <Box sx={{ p: 1, color: '#333', fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif' }}>
      {/* Hospital-Grade Header */}
      <Box sx={{ borderBottom: '2px solid #1976d2', pb: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 800, letterSpacing: '-0.5px' }}>
            SURGICAL REPORT
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
            Generated on: {reportDate}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
            MySurgeon EMR
          </Typography>
          <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 700 }}>
            CLINICAL DOCUMENTATION SYSTEM
          </Typography>
        </Box>
      </Box>

      {/* Patient Identity Card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 800, mb: 1, display: 'block', textTransform: 'uppercase' }}>
          Patient Identity Context
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Patient Name</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{patientName || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Age</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{age || '—'}</Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Gender</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, textTransform: 'capitalize' }}>{gender || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>AMKA / Social ID</Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{amka || '—'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Operation Basics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: '#1976d2', fontWeight: 800, mb: 2, textTransform: 'uppercase', borderBottom: '1px solid #eee', pb: 0.5 }}>
          Surgery Summary
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Operation Description</Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1976d2' }}>{operation.operation_type || operation.type || '—'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Date of Surgery</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{surgeryDateStr}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Location (Hospital)</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{operation.hospital || '—'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Primary Surgeon</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>Dr. {operation.surgeon || 'N/A'}</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Surgical Findings & Procedure Details</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{operation.procedure_details || operation.findings || '—'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Complications (if any)</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{operation.complications || '—'}</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Final Outcome / Clinical Status</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{operation.outcome || '—'}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#777', textTransform: 'uppercase' }}>Additional Clinical Notes</Typography>
            <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{operation.my_notes || operation.notes || '—'}</Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Signature Section */}
      <Box sx={{ mt: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ width: 250, textAlign: 'center' }}>
          <Box sx={{ borderBottom: '1px solid #333', mb: 1, height: 40 }} />
          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>AUTHORIZED SURGEON SIGNATURE</Typography>
          <Typography sx={{ fontSize: '10px', color: '#666' }}>Dated: {reportDate}</Typography>
        </Box>
      </Box>

      {/* Footer Info */}
      <Box sx={{ pt: 2, borderTop: '1px dashed #e0e0e0', mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: '#aaa' }}>
          Patient ID: {operation.id_pt} | Internal ID: {operation.id_op} | Confidential Surgical Document
        </Typography>
      </Box>
    </Box>
  );
};

const OperationsTab = ({ 
  patientId, 
  apiClient, 
  patientName = '', 
  age = '', 
  gender = '', 
  amka = '', 
  phone = '', 
  address = '', 
  diagnosis = '' 
}) => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('operation_date');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('clinicalViewMode') || 'card');

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem('clinicalViewMode', newMode);
    }
  };

  const loadOperations = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setError('');

    try {
      const response = await operationAPI.getByPatientId(patientId);
      setOperations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[OperationsTab] Error loading operations:', err);
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load operations');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  useOperationsSubscription(patientId, loadOperations);

  const handleAdd = () => {
    setEditingOperation(null);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleEdit = (operation) => {
    setEditingOperation(operation);
    setViewOnly(false);
    setDialogOpen(true);
  };

  const handleView = (operation) => {
    setEditingOperation(operation);
    setViewOnly(true);
    setDialogOpen(true);
  };

  const handlePrint = (operation) => {
    const reportDate = formatDate(new Date());
    const surgeryDateStr = formatDate(operation.operation_date);

    const reportHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Surgical Report - ${patientName}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body { font-family: 'Inter', sans-serif; line-height: 1.6; color: #333; padding: 40px; }
          .header { border-bottom: 2px solid #1976d2; padding-bottom: 15px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
          .hospital-name { font-size: 24px; font-weight: 800; color: #1976d2; margin: 0; }
          .identity-card { background: #f8f9fa; border: 1px solid #e0e0e0; padding: 20px; border-radius: 8px; margin-bottom: 30px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .field-label { font-size: 10px; font-weight: 800; color: #777; text-transform: uppercase; margin-bottom: 4px; }
          .field-value { font-size: 14px; font-weight: 600; }
          .section-title { font-size: 12px; font-weight: 800; color: #1976d2; text-transform: uppercase; border-bottom: 1px solid #1976d2; padding-bottom: 5px; margin-bottom: 15px; margin-top: 30px; }
          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
          .full-row { grid-column: span 2; }
          .op-type-highlight { font-size: 18px; font-weight: 800; color: #1976d2; }
          .signature-area { margin-top: 60px; display: flex; justify-content: flex-end; }
          .signature-line { width: 250px; border-top: 1px solid #333; text-align: center; padding-top: 5px; font-size: 11px; font-weight: 700; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 class="hospital-name">SURGICAL REPORT</h1>
            <div style="font-size: 12px; color: #666; font-weight: 600;">Generated on: ${reportDate}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 18px; font-weight: 700;">MySurgeon EMR</div>
            <div style="font-size: 10px; font-weight: 700; color: #1976d2; text-transform: uppercase;">Clinical Documentation System</div>
          </div>
        </div>

        <div class="identity-card">
          <div>
            <div class="field-label">Patient Name</div>
            <div class="field-value">${patientName || '—'}</div>
          </div>
          <div>
            <div class="field-label">Age / Gender</div>
            <div class="field-value">${age || '—'} / ${gender || '—'}</div>
          </div>
          <div>
            <div class="field-label">AMKA / Social ID</div>
            <div class="field-value">${amka || '—'}</div>
          </div>
        </div>

        <div class="section-title">Surgery Informatics</div>
        <div class="details-grid">
          <div>
            <div class="field-label">Operation Description</div>
            <div class="op-type-highlight">${operation.operation_type || operation.type || '—'}</div>
          </div>
          <div>
            <div class="field-label">Surgery Date</div>
            <div class="field-value">${surgeryDateStr}</div>
          </div>
          <div>
            <div class="field-label">Executing Surgeon</div>
            <div class="field-value">Dr. ${operation.surgeon || 'N/A'}</div>
          </div>
          <div>
            <div class="field-label">Hospital Facility</div>
            <div class="field-value">${operation.hospital || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Surgical Findings & Procedure</div>
            <div class="field-value" style="white-space: pre-wrap;">${operation.procedure_details || operation.findings || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Complications</div>
            <div class="field-value" style="white-space: pre-wrap;">${operation.complications || 'None Recorded'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Clinical Outcome</div>
            <div class="field-value" style="white-space: pre-wrap;">${operation.outcome || '—'}</div>
          </div>
          <div class="full-row">
            <div class="field-label">Attending Physician Notes</div>
            <div class="field-value" style="white-space: pre-wrap;">${operation.my_notes || operation.notes || '—'}</div>
          </div>
        </div>

        <div class="signature-area">
          <div class="signature-line">
            AUTHORIZED SURGEON SIGNATURE
            <div style="font-weight: 400; font-size: 9px; margin-top: 4px;">Dated: ${reportDate}</div>
          </div>
        </div>

        <div style="margin-top: 40px; border-top: 1px dashed #ccc; padding-top: 20px; text-align: center; font-size: 10px; color: #aaa;">
          Confidential Medical Record | Surgical Documentation | MySurgeon EMR Internal Report
        </div>
      </body>
      </html>
    `;

    const fileName = `${patientName || 'Patient'}_SurgicalReport_${operation.operation_type || 'Record'}`;
    PlatformService.print(reportHtml, fileName);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this operation?')) return;
    if (!id) return;

    try {
      await operationAPI.delete(id);
      setOperations((prev) => prev.filter((o) => resolveOperationId(o) !== id));
    } catch (err) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to delete operation');
    }
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const operationData = {
        id_pt: patientId,
        operation_date: values.operation_date || null,
        operation_type: values.operation_type || null,
        surgeon: values.surgeon || null,
        hospital: values.hospital || null,
        procedure_details: values.procedure_details || null,
        complications: values.complications || null,
        outcome: values.outcome || null,
        my_notes: values.my_notes || null,
      };

      if (editingOperation) {
        const opId = resolveOperationId(editingOperation);
        const response = await operationAPI.update(opId, operationData);
        const updated = response.data;
        const updatedId = resolveOperationId(updated) ?? opId;
        setOperations((prev) => prev.map((o) => (resolveOperationId(o) === updatedId ? updated : o)));
      } else {
        const response = await operationAPI.create(operationData);
        const created = response.data;
        setOperations((prev) => [created, ...prev]);
      }

      setDialogOpen(false);
    } catch (err) {
      setStatus(err.response?.data?.error ?? err.message ?? 'Failed to save operation');
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = editingOperation
    ? {
        operation_date: toDateInputValue(editingOperation.operation_date ?? editingOperation.date),
        operation_type: editingOperation.operation_type ?? editingOperation.type ?? '',
        surgeon: editingOperation.surgeon ?? '',
        hospital: editingOperation.hospital ?? '',
        procedure_details: editingOperation.procedure_details ?? editingOperation.findings ?? '',
        complications: editingOperation.complications ?? '',
        outcome: editingOperation.outcome ?? '',
        my_notes: editingOperation.my_notes ?? editingOperation.notes ?? '',
      }
    : {
        operation_date: new Date().toISOString().split('T')[0],
        operation_type: '',
        surgeon: '',
        hospital: '',
        procedure_details: '',
        complications: '',
        outcome: '',
        my_notes: '',
      };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedOperations = useMemo(() => {
    return [...operations].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'operation_date') {
        aValue = parseDate(aValue)?.getTime() || 0;
        bValue = parseDate(bValue)?.getTime() || 0;
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [operations, order, orderBy]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Operations</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              value={orderBy}
              label="Sort By"
              onChange={(e) => setOrderBy(e.target.value)}
            >
              <MenuItem value="operation_date">Date</MenuItem>
              <MenuItem value="operation_type">Type</MenuItem>
              <MenuItem value="surgeon">Surgeon</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={`Sort ${order === 'asc' ? 'Descending' : 'Ascending'}`}>
            <IconButton onClick={() => setOrder(order === 'asc' ? 'desc' : 'asc')} color="primary">
              <TableSortLabel active direction={order} hideSortIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh operations">
            <IconButton onClick={loadOperations} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            color="primary"
          >
            <ToggleButton value="card" aria-label="card view">
              <Tooltip title="Card View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <Tooltip title="Table View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Operation
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {operations.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No operations recorded yet
          </Typography>
        </Paper>
      ) : viewMode === 'card' ? (
        <Grid container spacing={2}>
          {sortedOperations.map((operation, index) => {
            const operationId = resolveOperationId(operation) ?? index;
            const opType = (operation.operation_type ?? operation.type) || 'Surgical Procedure';
            
            return (
              <Grid item xs={12} sm={6} md={4} key={operationId}>
                <ClinicalDataCard
                  title={opType}
                  subtitle={operation.surgeon ? `Dr. ${operation.surgeon}` : null}
                  date={formatOperationDate(operation.operation_date ?? operation.date)}
                  type="operation"
                  content={[
                    { label: 'Hospital', value: operation.hospital },
                    { label: 'Details', value: operation.procedure_details || operation.findings },
                    { label: 'Outcome', value: operation.outcome },
                  ]}
                  onEdit={() => handleEdit(operation)}
                  onDelete={() => handleDelete(operationId)}
                  onPreview={() => handleView(operation)}
                  onPrint={() => handlePrint(operation)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', py: 1.5 }}>
                  <TableSortLabel
                    active={orderBy === 'operation_date'}
                    direction={orderBy === 'operation_date' ? order : 'asc'}
                    onClick={() => handleRequestSort('operation_date')}
                  >
                    DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>
                  <TableSortLabel
                    active={orderBy === 'operation_type'}
                    direction={orderBy === 'operation_type' ? order : 'asc'}
                    onClick={() => handleRequestSort('operation_type')}
                  >
                    TYPE
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>SURGEON</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main' }}>HOSPITAL</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'primary.main', textAlign: 'center' }}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedOperations.map((operation, index) => {
                const operationId = resolveOperationId(operation) ?? index;
                return (
                  <TableRow key={operationId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 600 }}>{formatOperationDate(operation.operation_date ?? operation.date)}</TableCell>
                    <TableCell>{operation.operation_type ?? operation.type ?? '—'}</TableCell>
                    <TableCell>{operation.surgeon ? `Dr. ${operation.surgeon}` : '—'}</TableCell>
                    <TableCell>{operation.hospital || '—'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handleView(operation)} color="primary">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print">
                          <IconButton size="small" onClick={() => handlePrint(operation)} color="info">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(operation)} color="secondary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(operationId)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, bgcolor: viewOnly ? '#f8f9fa' : 'transparent' }}>
          {viewOnly ? 'Surgical Report Preview' : editingOperation ? 'Edit Operation' : 'Add Operation'}
        </DialogTitle>
        
        {viewOnly ? (
          <>
            <DialogContent dividers>
              <OperationReportView 
                operation={editingOperation}
                patientName={patientName}
                age={age}
                gender={gender}
                amka={amka}
                phone={phone}
                address={address}
                diagnosis={diagnosis}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
              <Button onClick={() => setDialogOpen(false)} variant="outlined">Close</Button>
              <Button 
                variant="contained" 
                startIcon={<PrintIcon />} 
                onClick={() => handlePrint(editingOperation)}
              >
                Print Report
              </Button>
            </DialogActions>
          </>
        ) : (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, touched, errors, handleBlur, status, isSubmitting }) => (
              <Form>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="operation_date"
                        label="Operation Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={values.operation_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.operation_date && Boolean(errors.operation_date)}
                        helperText={touched.operation_date && errors.operation_date}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="operation_type"
                        label="Operation Type"
                        value={values.operation_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.operation_type && Boolean(errors.operation_type)}
                        helperText={touched.operation_type && errors.operation_type}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="surgeon"
                        label="Surgeon"
                        value={values.surgeon}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="hospital"
                        label="Hospital"
                        value={values.hospital}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="procedure_details"
                        label="Procedure Details"
                        value={values.procedure_details}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        minRows={4}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="complications"
                        label="Complications"
                        value={values.complications}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="outcome"
                        label="Outcome"
                        value={values.outcome}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="my_notes"
                        label="Notes"
                        value={values.my_notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                  {status && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {status}
                    </Alert>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                  <Button onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </Box>
  );
};

export default OperationsTab;
